import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { ReportTaskStatusBadge, ReportUrgencyBadge } from '../../components/reports/ReportBadges';
import { ActionTipButton } from '../../components/common/ActionTipButton';
import { DashboardAuditLogModal } from '../../components/dashboard/DashboardAuditLogModal';
import { DashboardChartsRow } from '../../components/dashboard/DashboardChartsRow';
import { DashboardDateFilter } from '../../components/dashboard/DashboardDateFilter';
import { DashboardKpiGrid } from '../../components/dashboard/DashboardKpiGrid';
import {
  fetchCaseAuditLogs,
  fetchDashboardCategoryChart,
  fetchDashboardStatusChart,
  fetchDashboardSummary,
  fetchDashboardUrgencyChart,
  searchDashboardCases,
  useDashboardMock,
} from '../../services/dashboard';
import type {
  DashboardAuditLogEntry,
  DashboardCaseRow,
  DashboardChartResponse,
  DashboardSummaryResponse,
  DashboardVariant,
} from '../../types/dashboard';
import { TASK_EMPTY_DISPLAY } from '../../data/taskStatus';
import { useTaskCaseDetailsNavigation } from '../../hooks/useTaskCaseDetailsNavigation';
import { dashboardReturn, openTaskCaseDetailsInNewTab } from '../task-case/taskCaseDetailsReturnNavigation';
import { formatDisplayDate } from '../../utils/dateFormat';
import { defaultDashboardDateRange, todayIsoDate } from '../../utils/reportDates';

/** Default reporting window for operational and SG dashboards on load. */
const DEFAULT_DASHBOARD_LOOKBACK_MONTHS = 1;

type AllocationMonitoringDashboardProps = {
  variant: DashboardVariant;
};

export function AllocationMonitoringDashboard({ variant }: AllocationMonitoringDashboardProps) {
  const { t } = useTranslation();
  const dashboardReturnState = useMemo(() => dashboardReturn(variant), [variant]);
  const { taskCasePath } = useTaskCaseDetailsNavigation({ returnState: dashboardReturnState });
  const i18nPrefix = variant === 'sg' ? 'sgDashboard' : 'operationalDashboard';
  const useMock = useDashboardMock(variant);

  const initialRange = useMemo(
    () => defaultDashboardDateRange(DEFAULT_DASHBOARD_LOOKBACK_MONTHS),
    [],
  );
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [appliedRange, setAppliedRange] = useState(initialRange);

  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null);
  const [urgencyChart, setUrgencyChart] = useState<DashboardChartResponse | null>(null);
  const [statusChart, setStatusChart] = useState<DashboardChartResponse | null>(null);
  const [categoryChart, setCategoryChart] = useState<DashboardChartResponse | null>(null);
  const [cases, setCases] = useState<DashboardCaseRow[]>([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [tableKey, setTableKey] = useState(0);

  const [casesPage, setCasesPage] = useState(1);
  const [casesPageSize, setCasesPageSize] = useState(10);
  const [casesTotal, setCasesTotal] = useState(0);
  const [casesTotalPages, setCasesTotalPages] = useState(1);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortKey, setSortKey] = useState('fileRef');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditFileRef, setAuditFileRef] = useState('');
  const [auditEntries, setAuditEntries] = useState<DashboardAuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const dateFromLabel = formatDisplayDate(appliedRange.from);
  const dateToLabel = formatDisplayDate(appliedRange.to);

  const loadDashboardMetrics = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const [summaryRes, urgencyRes, statusRes, categoryRes] = await Promise.all([
        fetchDashboardSummary(variant, appliedRange),
        fetchDashboardUrgencyChart(variant, appliedRange),
        fetchDashboardStatusChart(variant, appliedRange),
        fetchDashboardCategoryChart(variant, appliedRange),
      ]);
      setSummary(summaryRes);
      setUrgencyChart(urgencyRes);
      setStatusChart(statusRes);
      setCategoryChart(categoryRes);
    } catch (e) {
      setDashboardError(e instanceof Error ? e.message : t('common.failedToLoad'));
    } finally {
      setDashboardLoading(false);
    }
  }, [variant, appliedRange, t]);

  const loadCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const res = await searchDashboardCases(variant, {
        ...appliedRange,
        page: useMock ? 1 : casesPage,
        size: useMock ? 1000 : casesPageSize,
        sortBy: useMock ? undefined : sortKey,
        sortDirection: useMock ? undefined : sortDir === 'desc' ? 'DESC' : 'ASC',
        columnFilters: useMock ? undefined : columnFilters,
      });
      setCases(res.content);
      if (!useMock) {
        setCasesTotal(res.totalElements);
        setCasesTotalPages(res.totalPages);
      }
    } finally {
      setCasesLoading(false);
    }
  }, [variant, appliedRange, useMock, casesPage, casesPageSize, sortKey, sortDir, columnFilters]);

  useEffect(() => {
    void loadDashboardMetrics();
  }, [loadDashboardMetrics]);

  useEffect(() => {
    void loadCases();
  }, [loadCases]);

  const applyDateFilter = () => {
    setAppliedRange({ from: dateFrom, to: dateTo });
    setCasesPage(1);
    setTableKey((k) => k + 1);
  };

  const openCaseDetails = useCallback(
    (row: DashboardCaseRow) => {
      if (!row.caseRequestId) return;
      const path = taskCasePath({
        caseRequestId: row.caseRequestId,
        userTaskId: row.taskId,
      });
      openTaskCaseDetailsInNewTab(path, dashboardReturnState);
    },
    [taskCasePath, dashboardReturnState],
  );

  const openAuditLog = async (row: DashboardCaseRow) => {
    setAuditFileRef(row.fileRef);
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const entries = await fetchCaseAuditLogs(variant, row.caseRequestId);
      setAuditEntries(entries);
    } finally {
      setAuditLoading(false);
    }
  };

  const columns = useMemo<Column<DashboardCaseRow>[]>(() => {
    const caseDetailsTooltip =
      variant === 'sg'
        ? t('tasks.shared.updateTaskCaseDetails')
        : t('allocationDashboard.viewCaseDetails');

    const base: Column<DashboardCaseRow>[] = [
      {
        key: 'fileRef',
        header: t('allocationDashboard.colFileRef'),
        getValue: (r) => r.fileRef,
        render: (r) => <span className="rpt-td-ref">{r.fileRef}</span>,
      },
      { key: 'fileCategory', header: t('allocationDashboard.colFileCategory'), getValue: (r) => r.fileCategory },
      { key: 'subFileCategory', header: t('allocationDashboard.colSubFileCategory'), getValue: (r) => r.subFileCategory },
      { key: 'submittedAgency', header: t('allocationDashboard.colSubmittedAgency'), getValue: (r) => r.submittedAgency },
      {
        key: 'submittedAt',
        header: t('allocationDashboard.colSubmittedAt'),
        getValue: (r) => r.submittedAt,
        filterType: 'date-range',
        filterMaxDate: todayIsoDate(),
      },
      { key: 'workflowStage', header: t('allocationDashboard.colWorkflowStage'), getValue: (r) => r.workflowStage },
      { key: 'allocationOfficer', header: t('allocationDashboard.colAllocationOfficer'), getValue: (r) => r.allocationOfficer },
      {
        key: 'assignedOfficer',
        header: t('allocationDashboard.colAssignedOfficer'),
        getValue: (r) => r.assignedOfficer ?? TASK_EMPTY_DISPLAY,
      },
      { key: 'supervisor', header: t('allocationDashboard.colSupervisor'), getValue: (r) => r.supervisor },
      {
        key: 'stateAttorney',
        header: t('allocationDashboard.colStateAttorney'),
        getValue: (r) => r.stateAttorney ?? TASK_EMPTY_DISPLAY,
      },
    ];

    base.push(
      {
        key: 'taskStatus',
        header: t('allocationDashboard.colTaskStatus'),
        getValue: (r) => r.taskStatus,
        render: (r) =>
          r.taskStatus === TASK_EMPTY_DISPLAY ? (
            TASK_EMPTY_DISPLAY
          ) : (
            <ReportTaskStatusBadge status={r.taskStatus} />
          ),
      },
      {
        key: 'urgencyLevel',
        header: t('allocationDashboard.colUrgencyLevel'),
        getValue: (r) => r.urgencyLevel,
        render: (r) =>
          r.urgencyLevel === TASK_EMPTY_DISPLAY ? (
            TASK_EMPTY_DISPLAY
          ) : (
            <ReportUrgencyBadge level={r.urgencyLevel} />
          ),
      },
      {
        key: 'actions',
        header: t('allocationDashboard.colActions'),
        sortable: false,
        searchable: false,
        headerClassName: 'dt-actions-col',
        cellClassName: 'dt-actions-col',
        getValue: () => '',
        render: (r) => (
          <div className="act-wrap">
            <ActionTipButton
              tooltip={caseDetailsTooltip}
              onClick={() => openCaseDetails(r)}
            >
              {variant === 'sg' ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              )}
            </ActionTipButton>
            <ActionTipButton
              tooltip={t('allocationDashboard.viewCaseTimeline')}
              onClick={() => void openAuditLog(r)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M12 7v5l4 2" />
              </svg>
            </ActionTipButton>
          </div>
        ),
      },
    );

    return base;
  }, [openCaseDetails, t, variant]);

  return (
    <>
      <div className="bc">
        <span>{t(`${i18nPrefix}.breadcrumb`)}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t(`${i18nPrefix}.title`)}</div>
          {/* <div className="pg-sub">{t(`${i18nPrefix}.subtitle`)}</div> */}
        </div>
      </div>

      <DashboardDateFilter
        from={dateFrom}
        to={dateTo}
        onFromChange={setDateFrom}
        onToChange={setDateTo}
        onApply={applyDateFilter}
        appliedFrom={appliedRange.from}
        appliedTo={appliedRange.to}
      />

      {dashboardLoading ? (
        <div className="flt-card" style={{ marginBottom: 14 }}>
          {t('common.loading')}
        </div>
      ) : dashboardError ? (
        <div className="flt-card" style={{ marginBottom: 14 }} role="alert">
          {dashboardError}
        </div>
      ) : !summary || !urgencyChart || !statusChart || !categoryChart ? (
        <div className="flt-card" style={{ marginBottom: 14 }}>
          {t('common.noResultsFound')}
        </div>
      ) : (
        <>
          <DashboardKpiGrid
            kpis={summary.kpis}
            i18nPrefix={i18nPrefix}
            dateFromLabel={dateFromLabel}
            dateToLabel={dateToLabel}
          />
          <DashboardChartsRow
            urgencyChart={urgencyChart}
            statusChart={statusChart}
            categoryChart={categoryChart}
            i18nPrefix={i18nPrefix}
            dateFromLabel={dateFromLabel}
            dateToLabel={dateToLabel}
          />
        </>
      )}

      <div className="dash-panel">
        <div className="dash-panel__title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--t2)" strokeWidth="2" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18M9 21V9" />
          </svg>
          {t('allocationDashboard.caseTrackingPanel')}
        </div>

        <DataTable
          key={tableKey}
          title=""
          columns={columns}
          rows={cases}
          rowKey={(r) => r.caseRequestId}
          loading={casesLoading}
          defaultRowsPerPage={10}
          rowsPerPageOptions={[10, 30, 50]}
          footerCountNoun={t('allocationDashboard.casesNoun')}
          embedded
          serverPaging={
            useMock
              ? undefined
              : {
                  totalCount: casesTotal,
                  totalPages: casesTotalPages,
                  page: casesPage,
                  pageSize: casesPageSize,
                  onPageChange: setCasesPage,
                  onPageSizeChange: (size) => {
                    setCasesPageSize(size);
                    setCasesPage(1);
                  },
                }
          }
          onFiltersChange={
            useMock
              ? undefined
              : (filters) => {
                  setCasesPage(1);
                  setColumnFilters(filters);
                }
          }
          serverSort={
            useMock
              ? undefined
              : {
                  key: sortKey,
                  dir: sortDir,
                  onSortChange: (key, dir) => {
                    setSortKey(key);
                    setSortDir(dir);
                    setCasesPage(1);
                  },
                }
          }
        />
      </div>

      <DashboardAuditLogModal
        open={auditOpen}
        fileRef={auditFileRef}
        entries={auditEntries}
        loading={auditLoading}
        onClose={() => setAuditOpen(false)}
      />
    </>
  );
}
